import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { Database } from '@/integrations/supabase/types';
import type { InfluencerRecipient } from '@/types/recipient.types';

type RpcRow =
  Database['public']['Functions']['get_influencer_recipients_paginated']['Returns'][number];

const PAGE_SIZE = 50;

function mapRow(row: RpcRow): InfluencerRecipient {
  return {
    id: row.id,
    sortName: row.sort_name,
    name: row.name,
    handle: row.handle,
    email: row.email.trim(),
    phone: row.phone,
    platform: row.platform,
    avatar: row.avatar,
    type: 'influencer',
    isDeliverable: row.is_deliverable,
  };
}

export interface UseInfluencerRecipientsOptions {
  searchTerm?: string;
  deliverableOnly?: boolean;
}

export function useInfluencerRecipients({
  searchTerm = '',
  deliverableOnly = false,
}: UseInfluencerRecipientsOptions = {}) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const trimmedSearch = searchTerm.trim();

  return useInfiniteQuery({
    queryKey: ['influencer-recipients', orgId, trimmedSearch, deliverableOnly],
    queryFn: async ({ pageParam }): Promise<{ items: InfluencerRecipient[]; hasMore: boolean }> => {
      if (!orgId) return { items: [], hasMore: false };

      const { data, error } = await supabase.rpc(
        'get_influencer_recipients_paginated',
        {
          p_org_id: orgId,
          p_search_term: trimmedSearch || null,
          p_after_sort_name: pageParam?.afterSortName ?? null,
          p_after_id: pageParam?.afterId ?? null,
          p_page_size: PAGE_SIZE,
          p_deliverable_only: deliverableOnly,
        },
      );

      if (error) throw error;

      const rows = (data ?? []) as RpcRow[];
      const items = rows.map(mapRow);
      const hasMore = rows.length > 0 && rows[rows.length - 1].has_more;

      return { items, hasMore };
    },
    initialPageParam: null as { afterSortName: string; afterId: string } | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || lastPage.items.length === 0) return undefined;
      const last = lastPage.items[lastPage.items.length - 1];
      return { afterSortName: last.sortName, afterId: last.id };
    },
    enabled: !!orgId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
